import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, QrCode, Clock, Heart, Users, Smartphone, X } from "lucide-react";
import { CTA_PRICE } from "../../config/pricing";
import { useLanguage } from "../../contexts/LanguageContext";

const emergencyLocketFeaturesConfig = [
  { id: "instant-access", icon: QrCode, titleKey: "features.f1Title", descKey: "features.f1Desc", detailDescKey: "features.f1DetailDesc", ctaKey: "features.f1Cta", iconBg: "bg-red-100", textColor: "text-red-700" },
  { id: "medical-info", icon: Heart, titleKey: "features.f2Title", descKey: "features.f2Desc", detailDescKey: "features.f2DetailDesc", ctaKey: "features.f2Cta", iconBg: "bg-pink-100", textColor: "text-pink-700" },
  { id: "24-7-available", icon: Clock, titleKey: "features.f3Title", descKey: "features.f3Desc", detailDescKey: "features.f3DetailDesc", ctaKey: "features.f3Cta", iconBg: "bg-blue-100", textColor: "text-blue-700" },
  { id: "privacy-control", icon: Shield, titleKey: "features.f4Title", descKey: "features.f4Desc", detailDescKey: "features.f4DetailDesc", ctaKey: "features.f4Cta", iconBg: "bg-purple-100", textColor: "text-purple-700" },
  { id: "family-ready", icon: Users, titleKey: "features.f5Title", descKey: "features.f5Desc", detailDescKey: "features.f5DetailDesc", ctaKey: "features.f5Cta", iconBg: "bg-green-100", textColor: "text-green-700" },
  { id: "no-app-needed", icon: Smartphone, titleKey: "features.f6Title", descKey: "features.f6Desc", detailDescKey: "features.f6DetailDesc", ctaKey: "features.f6Cta", iconBg: "bg-orange-100", textColor: "text-orange-700" },
];

const EmergencyLocketFeatures = ({ sectionTitle, sectionSubtitle }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeFeatureModal, setActiveFeatureModal] = useState(null);

  const features = useMemo(
    () =>
      emergencyLocketFeaturesConfig.map((c) => ({
        ...c,
        title: t(c.titleKey),
        description: t(c.descKey),
        detailDesc: t(c.detailDescKey),
        cta: t(c.ctaKey),
      })),
    [t]
  );

  return (
    <section className="py-10 md:py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">{sectionTitle}</h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">{sectionSubtitle}</p>
        </div>

        {/* Feature Card Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.id}
                onClick={() => setActiveFeatureModal(idx)}
                className="bg-white border border-slate-200 rounded-lg p-4 cursor-pointer hover:shadow-md hover:border-red-200 transition-shadow duration-150 flex flex-col"
              >
                <div className={`w-10 h-10 ${feature.iconBg} rounded-lg flex items-center justify-center mb-3`}>
                  <Icon className={`h-5 w-5 ${feature.textColor}`} />
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-1">{feature.title}</h3>
                <p className="text-sm text-slate-600 mb-3 flex-1">{feature.description}</p>
                <span className="text-xs font-semibold text-red-600">{t("features.buyNow")} &rarr;</span>
              </div>
            );
          })}
        </div>

        {/* Feature Detail Modal */}
        {activeFeatureModal !== null && (() => {
          const feature = features[activeFeatureModal];
          const Icon = feature.icon;
          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
              onClick={() => setActiveFeatureModal(null)}
            >
              <div
                className="bg-white rounded-lg shadow-md max-w-md w-full p-5 border border-slate-200 max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 ${feature.iconBg} rounded-lg flex items-center justify-center`}>
                      <Icon className={`h-5 w-5 ${feature.textColor}`} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">{feature.title}</h3>
                  </div>
                  <button onClick={() => setActiveFeatureModal(null)} className="text-slate-400 hover:text-slate-600 p-1">
                    <X size={20} />
                  </button>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">{feature.detailDesc}</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm font-semibold text-red-700">{feature.cta}</p>
                </div>
                <button
                  onClick={() => {
                    setActiveFeatureModal(null);
                    navigate('/order');
                  }}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg transition-colors duration-150 text-sm"
                >
                  {t("features.buyNow")} &mdash; {CTA_PRICE}
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </section>
  );
};

export default EmergencyLocketFeatures;
export { emergencyLocketFeaturesConfig as emergencyLocketFeatures };
